import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Eye, Pencil, Trash2, ChevronDown,
  Loader2, FolderKanban,
} from 'lucide-react';

import { toast } from 'react-toastify';

import { Button, Card, Badge } from '../../ui-v2';
import Paginacion from '../../comunes/Paginacion';
import ConfirmacionEliminar from '../../ui/ConfirmacionEliminar';
import AccionesCampanaCancelada from '../campanas/AccionesCampanaCancelada';
import {
  getCampanas, deleteCampana, cambiarEstadoCampana,
  contarParticipacionesCanceladasSistema,
} from '../api';

const ESTADOS_PRINCIPALES = [
  { value: '', label: 'Todas' },
  { value: 'Activa', label: 'Activas' },
  { value: 'Borrador', label: 'Borrador' },
  { value: 'Completada', label: 'Completadas' },
];

const ESTADOS_EXTRA = [
  { value: 'Pausada', label: 'Pausadas' },
  { value: 'Cancelada', label: 'Canceladas' },
];

const BADGE_VARIANT = {
  Activa: 'success',
  Borrador: 'secondary',
  Pausada: 'warning',
  Completada: 'accent',
  Cancelada: 'destructive',
};

const PAGE_SIZE = 10;

function formatPeriodo(inicio, fin) {
  const opts = { day: '2-digit', month: 'short' };
  if (!inicio && !fin) return '—';
  if (!inicio && fin) return `Hasta ${new Date(fin).toLocaleDateString('es-ES', opts)}`;
  if (inicio && !fin) return `Desde ${new Date(inicio).toLocaleDateString('es-ES', opts)}`;
  const a = new Date(inicio).toLocaleDateString('es-ES', opts);
  const b = new Date(fin).toLocaleDateString('es-ES', opts);
  return `${a} – ${b}`;
}

function useDebouncedValue(value, ms = 350) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return d;
}

export default function ListaCampanas() {
  const navigate = useNavigate();

  const [estado, setEstado] = useState('');
  const [qInput, setQInput] = useState('');
  const q = useDebouncedValue(qInput, 350);
  const [page, setPage] = useState(1);

  const [campanas, setCampanas] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [extraOpen, setExtraOpen] = useState(false);
  const extraRef = useRef(null);

  const [borrarTarget, setBorrarTarget] = useState(null);
  const [borrarError, setBorrarError] = useState(null);

  useEffect(() => { setPage(1); }, [estado, q]);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (estado) params.estado = estado;
      if (q) params.q = q;
      const res = await getCampanas(params);
      setCampanas(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      console.error('[ListaCampanas]', e);
      setError(e?.response?.data?.error || 'No se pudieron cargar las campañas.');
    } finally { setLoading(false); }
  }, [page, estado, q]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    function onDoc(e) {
      if (extraRef.current && !extraRef.current.contains(e.target)) setExtraOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // --- Navegación contextual ---

  function rutaEdicion(c) {
    if (c.estado === 'Borrador') return `/citas-tecnicas/campanas/${c.id}/wizard`;
    if (c.estado === 'Activa' || c.estado === 'Pausada') {
      return `/citas-tecnicas/campanas/${c.id}/editar`;
    }
    return null; // Completada / Cancelada: no editables
  }

  function rutaResumen(c) {
    return `/citas-tecnicas/campanas/${c.id}/resumen`;
  }

  function clickFila(c) {
    // Click en fila -> siempre al resumen.
    // El editor se abre desde el lápiz (botón explícito).
    // Excepción: si es Borrador, va al wizard (no hay resumen útil de algo sin datos).
    if (c.estado === 'Borrador') {
      navigate(`/citas-tecnicas/campanas/${c.id}/wizard`);
    } else {
      navigate(rutaResumen(c));
    }
  }

  function clickEditar(c) {
    const r = rutaEdicion(c);
    if (r) navigate(r);
  }

  function clickVer(c) {
    navigate(rutaResumen(c));
  }

  function nuevaCampana() {
    navigate('/citas-tecnicas/campanas/nueva');
  }

  function pedirBorrar(campana) {
    setBorrarError(null);
    setBorrarTarget({ id: campana.id, titulo: campana.titulo });
  }

  async function confirmarBorrar() {
    if (!borrarTarget) return;
    setBorrarError(null);
    try {
      await deleteCampana(borrarTarget.id);
      setBorrarTarget(null);
      if (campanas.length === 1 && page > 1) setPage(page - 1);
      else await cargar();
    } catch (e) {
      console.error('[deleteCampana]', e);
      setBorrarError(e?.response?.data?.error || 'No se pudo borrar la campaña.');
    }
  }

  function cancelarBorrar() {
    setBorrarTarget(null); setBorrarError(null);
  }

  // Recarga tras acciones sobre una campaña Cancelada (eliminar / reactivar).
  async function recargarLista() {
    if (campanas.length === 1 && page > 1) setPage(page - 1);
    else await cargar();
  }

  function handleCampanaReactivada(resp) {
    let msg = 'Campaña reactivada.';
    if (resp?.participaciones_revividas > 0) {
      msg += ` Se restauraron ${resp.participaciones_revividas} participaciones.`;
    }
    if (resp?.aviso) msg += ` ${resp.aviso}`;
    toast.success(msg);
    recargarLista();
  }

  const estadoExtraSeleccionado = ESTADOS_EXTRA.find((e) => e.value === estado);

  return (
    <div className="px-6 py-5 min-h-full">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">Campañas</h1>
          <p className="text-sm text-slate-500 mt-1">
            Gestión de campañas de intervención
            {!loading && (
              <>
                {' · '}
                <span className="font-medium text-slate-700">
                  {total} {total === 1 ? 'campaña' : 'campañas'}
                </span>
              </>
            )}
          </p>
        </div>

        <Button onClick={nuevaCampana} className="shrink-0">
          <Plus className="w-4 h-4" />
          Nueva campaña
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {ESTADOS_PRINCIPALES.map((opt) => {
          const activo = estado === opt.value;
          return (
            <button
              key={opt.value || 'todas'}
              onClick={() => setEstado(opt.value)}
              className={
                'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ' +
                (activo
                  ? 'bg-slate-900 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50')
              }
            >
              {opt.label}
            </button>
          );
        })}

        <div className="relative" ref={extraRef}>
          <button
            onClick={() => setExtraOpen((v) => !v)}
            className={
              'px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors ' +
              (estadoExtraSeleccionado
                ? 'bg-slate-900 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50')
            }
            aria-haspopup="menu"
            aria-expanded={extraOpen}
          >
            {estadoExtraSeleccionado ? estadoExtraSeleccionado.label : 'Más…'}
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {extraOpen && (
            <div
              role="menu"
              className="absolute left-0 top-full mt-1 min-w-[10rem] bg-white border border-slate-200 rounded-lg shadow-sm z-20 py-1"
            >
              {ESTADOS_EXTRA.map((opt) => (
                <button
                  key={opt.value}
                  role="menuitem"
                  onClick={() => { setEstado(opt.value); setExtraOpen(false); }}
                  className={
                    'w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ' +
                    (estado === opt.value ? 'text-primary font-medium' : 'text-slate-600')
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="ml-auto relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
          <input
            type="text"
            placeholder="Buscar campaña…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            className="w-56 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
          />
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        {loading ? (
          <EstadoMensaje icon={<Loader2 className="w-5 h-5 animate-spin" />}>
            Cargando campañas…
          </EstadoMensaje>
        ) : error ? (
          <EstadoMensaje tono="error">
            {error}
            <div className="mt-2">
              <Button variant="outline" size="sm" onClick={cargar}>Reintentar</Button>
            </div>
          </EstadoMensaje>
        ) : campanas.length === 0 ? (
          <EstadoMensaje icon={<FolderKanban className="w-6 h-6 text-slate-400" />}>
            {estado || q
              ? 'No hay campañas que coincidan con los filtros aplicados.'
              : 'Aún no hay campañas creadas.'}
          </EstadoMensaje>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                <th className="text-left font-medium px-4 py-2.5">Campaña</th>
                <th className="text-left font-medium px-4 py-2.5">Periodo</th>
                <th className="text-left font-medium px-4 py-2.5">Progreso</th>
                <th className="text-left font-medium px-4 py-2.5">Estado</th>
                <th className="text-right font-medium px-4 py-2.5">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {campanas.map((c) => (
                <FilaCampana
                  key={c.id}
                  campana={c}
                  onAbrir={() => clickFila(c)}
                  onEditar={() => clickEditar(c)}
                  onVer={() => clickVer(c)}
                  onPedirBorrar={() => pedirBorrar(c)}
                  onReactivada={handleCampanaReactivada}
                  onEliminadaCancelada={recargarLista}
                />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {!loading && !error && totalPages > 1 && (
        <div className="mt-4 flex justify-end">
          <Paginacion
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </div>
      )}

      {borrarTarget && (
        <ConfirmacionEliminar
          open={!!borrarTarget}
          titulo="Borrar campaña"
          descripcion={
            borrarError
              ? borrarError
              : `Vas a borrar la campaña "${borrarTarget.titulo}". Esta acción no se puede deshacer. Si la campaña tiene citas reservadas, primero debes cancelarla.`
          }
          palabraConfirmacion={borrarTarget.titulo}
          textoBotonConfirmar="Borrar definitivamente"
          onClose={cancelarBorrar}
          onConfirm={confirmarBorrar}
        />
      )}
    </div>
  );
}

function FilaCampana({ campana, onAbrir, onEditar, onVer, onPedirBorrar, onReactivada, onEliminadaCancelada }) {
  const variant = BADGE_VARIANT[campana.estado] || 'secondary';
  const progreso = campana.progreso || 0;
  const usuarios = campana.usuariosCount || 0;
  const tecnicos = campana.tecnicosCount || 0;
  const esBorrador = campana.estado === 'Borrador';
  const esActiva = campana.estado === 'Activa';
  const esPausada = campana.estado === 'Pausada';

  const editable = esBorrador || esActiva || esPausada;
  // Se puede borrar en estados no terminales (Borrador/Activa/Pausada). El
  // backend además bloquea si la campaña tiene citas reservadas (hay que
  // cancelarla primero). Las terminales (Completada/Cancelada) no se borran
  // desde aquí: son histórico.
  const puedeBorrar = esBorrador || esActiva || esPausada;

  return (
    <tr className="hover:bg-slate-50 cursor-pointer" onClick={onAbrir}>
      <td className="px-4 py-3">
        <div className="font-medium text-slate-800">{campana.titulo}</div>
        <div className="text-xs text-slate-400">
          {usuarios} {usuarios === 1 ? 'usuario' : 'usuarios'} · {tecnicos}{' '}
          {tecnicos === 1 ? 'técnico' : 'técnicos'}
        </div>
      </td>
      <td className="px-4 py-3 text-slate-600 text-xs">
        {formatPeriodo(campana.fecha_inicio, campana.fecha_limite)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-24 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-primary"
              style={{ width: `${progreso}%` }}
              aria-label={`${progreso}% completado`}
            />
          </div>
          <span className="text-xs text-slate-500 tabular-nums">{progreso}%</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <Badge variant={variant}>{campana.estado}</Badge>
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          <BotonAccion
            label="Ver resumen"
            onClick={onVer}
            icon={<Eye className="w-4 h-4 text-slate-500" />}
          />
          {editable ? (
            <BotonAccion
              label={esBorrador ? 'Continuar borrador' : 'Editar'}
              onClick={onEditar}
              icon={<Pencil className="w-4 h-4 text-slate-600" />}
            />
          ) : (
            <BotonAccionDeshabilitado
              label="No editable (terminal)"
              icon={<Pencil className="w-4 h-4 text-slate-300" />}
            />
          )}
          {puedeBorrar && (
            <BotonAccion
              label="Borrar"
              onClick={onPedirBorrar}
              icon={<Trash2 className="w-4 h-4 text-red-500" />}
              hoverClass="hover:bg-red-50"
            />
          )}
          {/* Fase 13 (Opción C) — solo se renderiza si la campaña es Cancelada */}
          <AccionesCampanaCancelada
            campana={campana}
            onReactivada={onReactivada}
            onEliminada={onEliminadaCancelada}
            eliminarCampana={deleteCampana}
            cambiarEstadoCampana={cambiarEstadoCampana}
            contarParticipacionesCanceladasSistema={contarParticipacionesCanceladasSistema}
          />
        </div>
      </td>
    </tr>
  );
}

function BotonAccion({ label, onClick, icon, hoverClass = 'hover:bg-slate-100' }) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`w-8 h-8 grid place-items-center rounded-lg ${hoverClass}`}
    >
      {icon}
    </button>
  );
}

function BotonAccionDeshabilitado({ label, icon }) {
  return (
    <div className="relative group">
      <button
        disabled
        aria-disabled="true"
        aria-label={label}
        className="w-8 h-8 grid place-items-center rounded-lg cursor-not-allowed"
      >
        {icon}
      </button>
      <span
        className="absolute right-0 top-full mt-1 px-2 py-1 text-xs bg-slate-900 text-white rounded
                   opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10"
      >
        {label}
      </span>
    </div>
  );
}

function EstadoMensaje({ icon, tono = 'info', children }) {
  const color = tono === 'error' ? 'text-red-600' : 'text-slate-500';
  return (
    <div className={'py-12 text-center text-sm ' + color}>
      {icon && <div className="flex justify-center mb-2">{icon}</div>}
      {children}
    </div>
  );
}
